import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { projectService } from '../services/project.service';

// Fonction fetcher pour SWR
const fetcher = async (url) => {
  const response = await projectService.getProjects();
  if (!response.success) {
    throw new Error(response.error);
  }
  // L'API retourne les projets dans data.results
  return response.data.results || response.data;
};

// Hook pour gérer les projets
export const useProjects = (params = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Utiliser SWR pour la mise en cache et la synchronisation
  const { data, mutate: mutateProjects, isValidating } = useSWR(
    'projects', // Clé simple pour éviter les appels multiples
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false, // Désactiver la revalidation automatique
      dedupingInterval: 300000, // 5 minutes
      errorRetryCount: 2,
      errorRetryInterval: 5000,
    }
  );

  // Récupérer les projets
  const getProjects = useCallback(async (newParams = {}) => {
    console.log('getProjects called manually');
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.getProjects({ ...params, ...newParams });
      if (response.success) {
        // Extraire les résultats de la réponse
        const projectsData = response.data.results || response.data;
        await mutateProjects(projectsData, false);
        return projectsData;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [params, mutateProjects]);

  // Créer un projet
  const createProject = useCallback(async (projectData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.createProject(projectData);
      if (response.success) {
        // Invalider le cache pour forcer une nouvelle récupération
        await mutateProjects();
        return response.data;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [mutateProjects]);

  // Mettre à jour un projet
  const updateProject = useCallback(async (projectId, projectData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.updateProject(projectId, projectData);
      if (response.success) {
        // Invalider le cache pour forcer une nouvelle récupération
        await mutateProjects();
        return response.data;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [mutateProjects]);

  // Supprimer un projet
  const deleteProject = useCallback(async (projectId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.deleteProject(projectId);
      if (response.success) {
        // Invalider le cache pour forcer une nouvelle récupération
        await mutateProjects();
        return response.data;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [mutateProjects]);

  return {
    projects: data,
    isLoading: isLoading || isValidating,
    error,
    getProjects,
    createProject,
    updateProject,
    deleteProject,
    mutateProjects,
  };
};

// Hook pour gérer un projet spécifique
export const useProject = (projectId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { data, mutate: mutateProject } = useSWR(
    projectId ? ['project', projectId] : null,
    () => projectService.getProjectById(projectId).then(res => res.success ? res.data : null),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
      errorRetryCount: 3,
    }
  );

  const getProject = useCallback(async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.getProjectById(projectId);
      if (response.success) {
        await mutateProject(response.data, false);
        return response.data;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, mutateProject]);

  return {
    project: data,
    isLoading,
    error,
    getProject,
    mutateProject,
  };
};

// Hook pour gérer les étapes d'un projet
export const useProjectSteps = (projectId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { data, mutate: mutateSteps } = useSWR(
    projectId ? ['project-steps', projectId] : null,
    () => projectService.getProjectSteps(projectId).then(res => res.success ? res.data : null),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
      errorRetryCount: 3,
    }
  );

  const getSteps = useCallback(async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectService.getProjectSteps(projectId);
      if (response.success) {
        await mutateSteps(response.data, false);
        return response.data;
      } else {
        setError(response.error);
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, mutateSteps]);

  return {
    steps: data,
    isLoading,
    error,
    getSteps,
    mutateSteps,
  };
}; 